// Messaging mutations. Each hook calls the API directly via `getApiClient()`
// and patches the QueryClient cache. No server actions involved.

import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type { ConversationId, UserId } from '@leanin/shared/ids';
import type { components } from '@leanin/api-client';
import {
  trackConversationRead,
  trackDirectConversationCreated,
  trackMessageSent,
  type ContactType,
} from '../analytics/events';
import { getApiClient } from '../api-client';
import { throwApiError, unwrap, unwrapVoid } from '../api-error';
import {
  patchItemInLists,
  prependItemToLists,
  removeItemFromLists,
  restoreSnapshots,
  type SnapshotSet,
} from '../query-cache-utils';
import {
  messagingKeys,
  type ConversationSummary,
  type MessageView,
} from '../queries/messaging';
import { useViewer } from '../viewer-context';

export type SendMessageInput = {
  conversationId: ConversationId;
  body: string;
  clientMessageId: string;
  contactType: ContactType;
  mediaKeys?: readonly string[];
};

export type ScheduleMessageInput = {
  conversationId: ConversationId;
  body: string;
  scheduledFor: Date;
  mediaKeys?: readonly string[];
};

export type CreateDirectConversationResult = {
  conversationId: ConversationId;
  // True when the API returned an existing conversation (409 dm_pair_exists).
  alreadyExisted: boolean;
};

type MessageEnvelope = components['schemas']['MessageEnvelope'];
type ScheduledMessageEnvelope = components['schemas']['ScheduledMessageEnvelope'];

// ---------- send / schedule ----------

export function useSendMessage() {
  const qc = useQueryClient();
  // The optimistic MessageView needs the viewer's UserId so the mapper's
  // `isMe = senderUserId === viewerId` check resolves correctly before
  // the server response lands. Authoring the optimistic with a placeholder
  // here (the previous bug used `senderUserId: ''`) renders the message
  // as if from the peer for the round-trip window — visible as a white
  // bubble that turns black after the server swap.
  const viewer = useViewer();
  return useMutation({
    mutationFn: async (input: SendMessageInput): Promise<MessageEnvelope> => {
      const client = getApiClient();
      return unwrap(
        await client.POST('/api/v1/conversations/{id}/messages', {
          params: { path: { id: input.conversationId } },
          body: {
            body: input.body,
            clientMessageId: input.clientMessageId,
            ...(input.mediaKeys !== undefined
              ? { mediaKeys: [...input.mediaKeys] }
              : {}),
          },
        }),
      );
    },
    // Optimistic prepend: a synthetic MessageView lands at the head of the
    // cached message list for this thread the instant the user hits Send.
    // The optimistic id carries `opt-<clientMessageId>` until the POST
    // resolves; onSuccess swaps it in-place for the canonical server row.
    onMutate: async (input): Promise<SnapshotSet & { optimisticId: string }> => {
      await qc.cancelQueries({
        queryKey: messagingKeys.messages(input.conversationId).queryKey,
      });
      const optimisticMessage: MessageView = {
        id: `opt-${input.clientMessageId}`,
        threadId: input.conversationId,
        senderUserId: viewer.id,
        body: input.body,
        attachments: [],
        createdAt: new Date().toISOString(),
        editedAt: null,
        readAt: null,
        hidden: false,
      };
      // `prependItemToLists` is head-page-only and dedupes by id, which is
      // exactly the optimistic-message contract. Using a "prepend to every
      // page" helper here would duplicate the optimistic across page
      // boundaries once message pagination is exercised.
      const snap = prependItemToLists<MessageView>(
        qc,
        messagingKeys.messages(input.conversationId).queryKey,
        optimisticMessage,
      );
      return { ...snap, optimisticId: optimisticMessage.id };
    },
    onError: (_err, _input, ctx) => {
      restoreSnapshots(qc, ctx);
    },
    onSuccess: (data, input, ctx) => {
      trackMessageSent({ contact_type: input.contactType });
      const sent = data.data;
      const optimisticId = ctx?.optimisticId;
      if (optimisticId === undefined) return;
      // Swap the optimistic for the canonical row, preserving its slot.
      patchItemInLists<MessageView>(
        qc,
        messagingKeys.messages(input.conversationId).queryKey,
        optimisticId,
        () => sent,
      );
    },
    onSettled: (_data, _err, input) => {
      // The messages cache is already canonical after onSuccess swapped the
      // optimistic. Don't re-fetch it here — the 5s poll picks up any peer
      // messages that landed concurrently. Only the inbox-side caches need
      // a refresh so the row preview / lastMessageAt reflect this send.
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
      qc.invalidateQueries({
        queryKey: messagingKeys.conversation(input.conversationId).queryKey,
      });
    },
  });
}

export function useScheduleMessage() {
  return useMutation({
    mutationFn: async (
      input: ScheduleMessageInput,
    ): Promise<ScheduledMessageEnvelope> => {
      const client = getApiClient();
      return unwrap(
        await client.POST('/api/v1/conversations/{cid}/schedule', {
          params: { path: { cid: input.conversationId } },
          body: {
            body: input.body,
            scheduledFor: input.scheduledFor.toISOString(),
            ...(input.mediaKeys !== undefined
              ? { mediaKeys: [...input.mediaKeys] }
              : {}),
          },
        }),
      );
    },
  });
}

// ---------- conversation lifecycle ----------

export function useFindOrCreateDirectConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      recipientId: UserId,
    ): Promise<CreateDirectConversationResult> => {
      const client = getApiClient();
      const { data, error, response } = await client.POST(
        '/api/v1/conversations',
        {
          body: { kind: 'dm', userIds: [recipientId] },
        },
      );
      if (data !== undefined) {
        return {
          conversationId: data.data.id as ConversationId,
          alreadyExisted: false,
        };
      }
      if (response.status === 409) {
        const body = error as {
          error?: { code?: string; details?: { conversationId?: string } };
        };
        const existing = body?.error?.details?.conversationId;
        if (existing !== undefined) {
          return {
            conversationId: existing as ConversationId,
            alreadyExisted: true,
          };
        }
      }
      throwApiError(error, response);
    },
    onSuccess: (data) => {
      if (!data.alreadyExisted) trackDirectConversationCreated();
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export type CreateGroupConversationInput = {
  userIds: readonly UserId[];
  title?: string;
};

export function useCreateGroupConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: CreateGroupConversationInput,
    ): Promise<{ conversationId: ConversationId }> => {
      const client = getApiClient();
      const data = unwrap(
        await client.POST('/api/v1/conversations', {
          body: {
            kind: 'group',
            userIds: [...input.userIds],
            ...(input.title !== undefined ? { title: input.title } : {}),
          },
        }),
      );
      return { conversationId: data.data.id as ConversationId };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: ConversationId): Promise<void> => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/conversations/{id}/delete', {
          params: { path: { id: conversationId } },
        }),
      );
    },
    onSuccess: (_data, conversationId) => {
      qc.removeQueries({
        queryKey: messagingKeys.conversation(conversationId).queryKey,
      });
      qc.removeQueries({
        queryKey: messagingKeys.messages(conversationId).queryKey,
      });
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export function usePatchConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      conversationId: ConversationId;
      title?: string;
      archived?: boolean;
    }) => {
      const client = getApiClient();
      return unwrap(
        await client.PATCH('/api/v1/conversations/{id}', {
          params: { path: { id: input.conversationId } },
          body: {
            ...(input.title !== undefined ? { title: input.title } : {}),
            ...(input.archived !== undefined
              ? { archived: input.archived }
              : {}),
          },
        }),
      );
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({
        queryKey: messagingKeys.conversation(input.conversationId).queryKey,
      });
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export function useAddConversationMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      conversationId: ConversationId;
      userIds: readonly string[];
    }) => {
      const client = getApiClient();
      return unwrap(
        await client.POST('/api/v1/conversations/{id}/members', {
          params: { path: { id: input.conversationId } },
          body: { userIds: [...input.userIds] },
        }),
      );
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({
        queryKey: messagingKeys.conversation(input.conversationId).queryKey,
      });
    },
  });
}

export function useRemoveConversationMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      conversationId: ConversationId;
      userId: string;
    }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE('/api/v1/conversations/{id}/members/{userId}', {
          params: {
            path: { id: input.conversationId, userId: input.userId },
          },
        }),
      );
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({
        queryKey: messagingKeys.conversation(input.conversationId).queryKey,
      });
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export function useTransferConversationOwnership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      conversationId: ConversationId;
      userId: string;
    }) => {
      const client = getApiClient();
      return unwrap(
        await client.POST('/api/v1/conversations/{id}/transfer-owner', {
          params: { path: { id: input.conversationId } },
          body: { userId: input.userId },
        }),
      );
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({
        queryKey: messagingKeys.conversation(input.conversationId).queryKey,
      });
    },
  });
}

// ---------- message requests (accept / decline / report) ----------
//
// A message request is a pending inbound DM from someone the viewer isn't
// connected with (`ConversationSummary.pendingRequest`). Acting on it removes
// the conversation from the Requests list: accept promotes it into the inbox,
// decline dismisses it, report flags + dismisses it. All three optimistically
// drop the row from every cached conversation list (the request only lives in
// the requests-scope list) and reconcile via `onSettled` — mirrors the group
// join-request approve/decline cache contract in `lib/mutations/groups.ts`.

type ReportMessageRequestBody =
  components['schemas']['ReportMessageRequestBody'];

export type ReportMessageRequestReason = NonNullable<
  ReportMessageRequestBody['reason']
>;

export type ReportMessageRequestInput = {
  conversationId: ConversationId;
  reason: ReportMessageRequestReason;
  reasonText?: string;
};

// The requests scope is a variant of the conversations list, so the cached
// rows are wire `ConversationSummary`s under the shared `conversations()`
// prefix. Removing by id is a no-op on lists that don't hold the row (the
// inbox), so a single prefix-wide remove cleanly drops it from the Requests
// list without touching the inbox.
function removeRequestFromCache(
  qc: QueryClient,
  conversationId: ConversationId,
): SnapshotSet {
  return removeItemFromLists<ConversationSummary>(
    qc,
    messagingKeys.conversations(),
    conversationId,
  );
}

function invalidateRequestScopes(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
  // The Requests tab badge reads from scope-counts, a separate key from the
  // conversations list — without this it stays stuck at the pre-decision total.
  qc.invalidateQueries({
    queryKey: [...messagingKeys.all, 'conversation-scope-counts'],
  });
}

function invalidateAfterRequestAccept(
  qc: QueryClient,
  conversationId: ConversationId,
): void {
  invalidateRequestScopes(qc);
  // The thread-detail query keys off `['messaging','conversation',id]`, a
  // sibling of the `['messaging','conversations']` list prefix above — so the
  // list invalidation does NOT reach it. Accepting a request flips the detail's
  // `pendingRequest`→false; without this the open thread stays stuck in its
  // received-request view (locked composer, sender card) until a remount.
  //
  // ACCEPT ONLY. Decline/report retire the viewer's membership, so refetching
  // the detail (or letting the messages poll keep running) 404s while the
  // thread is still selected under its confirmation dialog — those flows
  // instead suspend the thread queries (`enabled: false` via the retired state
  // in MessagesClient) and clean up via `removeRetiredRequestQueries` once the
  // selection clears.
  qc.invalidateQueries({
    queryKey: messagingKeys.conversation(conversationId).queryKey,
  });
}

// Post-retirement cache cleanup for a declined/reported request. Call AFTER
// the thread is deselected: removing an actively-observed query makes the
// observer rebuild-and-refetch it (a fresh 404), so removal is only safe once
// the detail/messages observers have moved off the id. Dropping the entries
// (rather than leaving them to gc) guarantees no surface can ever serve the
// retired conversation's stale "pending request" snapshot.
export function removeRetiredRequestQueries(
  qc: QueryClient,
  conversationId: ConversationId,
): void {
  qc.removeQueries({
    queryKey: messagingKeys.conversation(conversationId).queryKey,
  });
  qc.removeQueries({
    queryKey: messagingKeys.messages(conversationId).queryKey,
  });
}

export function useAcceptMessageRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: ConversationId): Promise<void> => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/conversations/{id}/request/accept', {
          params: { path: { id: conversationId } },
        }),
      );
    },
    onMutate: async (conversationId): Promise<SnapshotSet> => {
      await qc.cancelQueries({ queryKey: messagingKeys.conversations() });
      return removeRequestFromCache(qc, conversationId);
    },
    onError: (_err, _conversationId, ctx) => {
      restoreSnapshots(qc, ctx);
    },
    onSettled: (_data, _err, conversationId) =>
      invalidateAfterRequestAccept(qc, conversationId),
  });
}

export function useDeclineMessageRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: ConversationId): Promise<void> => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/conversations/{id}/request/decline', {
          params: { path: { id: conversationId } },
        }),
      );
    },
    onMutate: async (conversationId): Promise<SnapshotSet> => {
      await qc.cancelQueries({ queryKey: messagingKeys.conversations() });
      return removeRequestFromCache(qc, conversationId);
    },
    onError: (_err, _conversationId, ctx) => {
      restoreSnapshots(qc, ctx);
    },
    onSettled: () => invalidateRequestScopes(qc),
  });
}

export function useReportMessageRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReportMessageRequestInput): Promise<void> => {
      const client = getApiClient();
      const body: ReportMessageRequestBody = { reason: input.reason };
      if (
        input.reasonText !== undefined &&
        input.reasonText.trim().length > 0
      ) {
        body.reasonText = input.reasonText.trim();
      }
      unwrapVoid(
        await client.POST('/api/v1/conversations/{id}/request/report', {
          params: { path: { id: input.conversationId } },
          body,
        }),
      );
    },
    onMutate: async (input): Promise<SnapshotSet> => {
      await qc.cancelQueries({ queryKey: messagingKeys.conversations() });
      return removeRequestFromCache(qc, input.conversationId);
    },
    onError: (_err, _input, ctx) => {
      restoreSnapshots(qc, ctx);
    },
    onSettled: () => invalidateRequestScopes(qc),
  });
}

// ---------- read / unread / mute ----------

export function useMarkConversationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      conversationId: ConversationId;
      lastReadMessageId: string;
      contactType: ContactType;
    }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/conversations/{id}/read', {
          params: { path: { id: input.conversationId } },
          body: { lastReadMessageId: input.lastReadMessageId },
        }),
      );
    },
    onSuccess: (_data, input) => {
      trackConversationRead({ contact_type: input.contactType });
    },
    onSettled: (_data, _err, input) => {
      qc.invalidateQueries({
        queryKey: messagingKeys.conversation(input.conversationId).queryKey,
      });
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
      // The All/Unread tab badge reads from the scope-counts query, which is
      // a separate key from the conversations list. Without this, the badge
      // stays stuck at the pre-read total after auto-mark-read flips the row.
      qc.invalidateQueries({
        queryKey: [...messagingKeys.all, 'conversation-scope-counts'],
      });
    },
  });
}

export function useMarkConversationUnread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: ConversationId) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/conversations/{id}/unread', {
          params: { path: { id: conversationId } },
        }),
      );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
      qc.invalidateQueries({
        queryKey: [...messagingKeys.all, 'conversation-scope-counts'],
      });
    },
  });
}

export function useMuteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: ConversationId) => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/conversations/{id}/mute', {
          params: { path: { id: conversationId } },
        }),
      );
    },
    onSettled: (_data, _err, conversationId) => {
      qc.invalidateQueries({
        queryKey: messagingKeys.conversation(conversationId).queryKey,
      });
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export function useUnmuteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: ConversationId) => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE('/api/v1/conversations/{id}/mute', {
          params: { path: { id: conversationId } },
        }),
      );
    },
    onSettled: (_data, _err, conversationId) => {
      qc.invalidateQueries({
        queryKey: messagingKeys.conversation(conversationId).queryKey,
      });
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

// ---------- block / unblock ----------
//
// Blocking is a member-level state but we keep its invalidations local to
// messaging: the conversations list updates because the blocked peer's DM
// disappears, and the blocked-list query refetches. Feed-side visibility
// effects are not invalidated here (see slice rules).

export function useBlockMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; reason?: string }) => {
      const client = getApiClient();
      const body = input.reason
        ? { userId: input.userId, reason: input.reason }
        : { userId: input.userId };
      return unwrap(await client.POST('/api/v1/blocks', { body }));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
      // Opts-less prefix: `blocks` is keyed by { pageSize }, so refetch every
      // variant rather than only the default-args one.
      qc.invalidateQueries({ queryKey: [...messagingKeys.all, 'blocks'] });
    },
  });
}

export function useUnblockMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE('/api/v1/blocks/{userId}', {
          params: { path: { userId } },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
      // Opts-less prefix: `blocks` is keyed by { pageSize }, so refetch every
      // variant rather than only the default-args one.
      qc.invalidateQueries({ queryKey: [...messagingKeys.all, 'blocks'] });
    },
  });
}

// ---------- per-message edit / delete ----------

export function useUpdateMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { messageId: string; conversationId: ConversationId; body: string }) => {
      const client = getApiClient();
      const data = unwrap(
        await client.PATCH('/api/v1/messages/{id}', {
          params: { path: { id: input.messageId } },
          body: { body: input.body },
        }),
      );
      return data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: messagingKeys.messages(vars.conversationId).queryKey,
      });
      // The inbox preview reflects the conversation's last message body;
      // invalidate the list so a stale snippet doesn't linger.
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export function useDeleteMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { messageId: string; conversationId: ConversationId }) => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE('/api/v1/messages/{id}', {
          params: { path: { id: input.messageId } },
        }),
      );
      return input.messageId;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: messagingKeys.messages(vars.conversationId).queryKey,
      });
      qc.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}
